// REPLACE WITH:
import util from './util';
import { isMobile, isTablet } from 'react-device-detect';
import { getApiBase } from './axios';

// ─── Event type constants ────────────────────────────────────
export const TRAIL_EVENTS = {
    // Navigation
    PAGE_VIEW:              'page_view',
    TAB_CHANGE:             'tab_change',
    LINK_CLICK:             'link_click',

    // Auth
    LOGIN:                  'login',
    LOGOUT:                 'logout',

    // CRUD
    RECORD_CREATE:          'record_create',
    RECORD_UPDATE:          'record_update',
    RECORD_DELETE:          'record_delete',
    RECORD_VIEW:            'record_view',

    // Forms
    FORM_SUBMIT:            'form_submit',
    FORM_FIELD_CHANGE:      'form_field_change',
    FORM_VALIDATION_FAIL:   'form_validation_fail',

    // Files & Documents
    FILE_UPLOAD:            'file_upload',
    FILE_DOWNLOAD:          'file_download',
    FILE_DELETE:            'file_delete',
    DOCUMENT_VIEW:          'document_view',

    // Reports
    REPORT_GENERATE:        'report_generate',
    REPORT_EXPORT:          'report_export',

    // Search & Filter
    SEARCH:                 'search',
    FILTER_APPLY:           'filter_apply',

    // System / Errors
    ERROR:                  'error',
    API_FAIL:               'api_fail',

    // QoC-specific
    ASSESSMENT_SUBMIT:      'assessment_submit',
    ASSESSMENT_APPROVE:     'assessment_approve',
    ASSESSMENT_REJECT:      'assessment_reject',
    ASSESSMENT_REVISION:    'assessment_revision',
    CAP_CREATE:             'cap_create',
    CAP_UPDATE:             'cap_update',
    STANDARD_SETUP:         'standard_setup',
    HIE_SYNC:               'hie_sync',
    REPORT_PDF:             'report_pdf',
};

// ─── Module name constants (matches QoC nav structure) ───────
export const TRAIL_MODULES = {
    STANDARDS:          'Standards Setup',
    ASSESSMENTS:        'Assessments',
    APPROVAL_QUEUE:     'Approval Queue',
    CAP:                'Corrective Actions',
    ANALYTICS:          'Analytics',
    COMPLIANCE:         'Compliance',
    EXECUTIVE_DASH:     'Executive Dashboard',
    FACILITY_REPORT:    'Facility Report',
    DASHBOARD_BUILDER:  'Dashboard Builder',
    FACILITIES:         'Facilities',
    USERS:              'Users',
    HIE_SYNC:           'HIE Sync',
    AUDIT_SCHEDULES:    'Audit Schedules',
    PERIODS:            'Assessment Periods',
    SETTINGS:           'Settings',
    SURVEY:             'Customer Surveys',
    QUALITY_INDEX:      'Quality Index',
    KPI:                'KPI Targets',
};

// ─── Session singleton ────────────────────────────────────────
let _sessionId = null;
let _ip = 'unknown';
let _ipFetched = false;

const getSessionId = () => {
    if (!_sessionId) {
        _sessionId = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    }
    return _sessionId;
};

const fetchIp = async () => {
    if (_ipFetched) return _ip;
    try {
        const r = await fetch('https://ipapi.co/json/');
        const d = await r.json();
        _ip = d.ip || 'unknown';
    } catch {
        _ip = 'unknown';
    }
    _ipFetched = true;
    return _ip;
};

// Pre-fetch IP on module load
fetchIp();

// ─── Core log function ────────────────────────────────────────
/**
 * Fire a structured audit trail event to the QoC backend.
 * Always fire-and-forget — never await in UI code.
 *
 * @param {string} eventType  - One of TRAIL_EVENTS values
 * @param {string} module     - One of TRAIL_MODULES values (or free string)
 * @param {Object} details    - Free-form detail payload (will be JSON-stringified)
 * @param {string} [action]   - Short human-readable label e.g. "Approved assessment #42"
 */
export const logTrail = async (eventType, module, details = {}, action = '') => {
    try {
        const now = new Date();
        now.setHours(now.getHours() + 2); // SAST (UTC+2)

        const deviceType = isMobile ? 'Mobile' : isTablet ? 'Tablet' : 'Desktop';

        const payload = {
            user:       util.getLoggedName() || window.localStorage['name'] || 'unknown',
            eventType,
            module,
            action:     action || eventType,
            timestamp:  now.toISOString().slice(0, 19).replace('T', ' '), // MySQL datetime format
            ipAddress:  _ip,
            sessionId:  getSessionId(),
            deviceType,
            userAgent:  navigator.userAgent,
            url:        window.location.href,
            pathname:   window.location.pathname,
            referrer:   document.referrer || '',
            details:    JSON.stringify(details),
        };

        // Fire-and-forget POST — errors are silently swallowed
        fetch(getApiBase() + 'qoc/log', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'authorization': window.localStorage['token'] || '',
            },
            body: JSON.stringify(payload),
        }).catch(() => {});

    } catch (e) {
        // Logging must never break the UI
        console.debug('[activityTrail] silent error:', e);
    }
};

// ─── Convenience wrappers ─────────────────────────────────────

/** Page navigation */
export const trailPageView = (module, path) =>
    logTrail(TRAIL_EVENTS.PAGE_VIEW, module, { path });

/** Generic CRUD */
export const trailCreate = (module, entityName, entityId, extra = {}) =>
    logTrail(TRAIL_EVENTS.RECORD_CREATE, module, { entityName, entityId, ...extra }, `Created ${entityName}`);

export const trailUpdate = (module, entityName, entityId, changes = {}) =>
    logTrail(TRAIL_EVENTS.RECORD_UPDATE, module, { entityName, entityId, changes }, `Updated ${entityName}`);

export const trailDelete = (module, entityName, entityId, extra = {}) =>
    logTrail(TRAIL_EVENTS.RECORD_DELETE, module, { entityName, entityId, ...extra }, `Deleted ${entityName}`);

export const trailView = (module, entityName, entityId) =>
    logTrail(TRAIL_EVENTS.RECORD_VIEW, module, { entityName, entityId }, `Viewed ${entityName}`);

/** Files */
export const trailUpload = (module, fileName, fileSize, entityRef = '') =>
    logTrail(TRAIL_EVENTS.FILE_UPLOAD, module, { fileName, fileSize, entityRef }, `Uploaded ${fileName}`);

export const trailDownload = (module, fileName, entityRef = '') =>
    logTrail(TRAIL_EVENTS.FILE_DOWNLOAD, module, { fileName, entityRef }, `Downloaded ${fileName}`);

/** Search & Filter */
export const trailSearch = (module, query, resultCount = null) =>
    logTrail(TRAIL_EVENTS.SEARCH, module, { query, resultCount });

export const trailFilter = (module, filters) =>
    logTrail(TRAIL_EVENTS.FILTER_APPLY, module, { filters });

/** Navigation */
export const trailTabChange = (module, fromTab, toTab) =>
    logTrail(TRAIL_EVENTS.TAB_CHANGE, module, { fromTab, toTab });

/** Reports */
export const trailReportGenerate = (module, reportType, params = {}) =>
    logTrail(TRAIL_EVENTS.REPORT_GENERATE, module, { reportType, params }, `Generated ${reportType} report`);

export const trailReportExport = (module, reportType, format, params = {}) =>
    logTrail(TRAIL_EVENTS.REPORT_EXPORT, module, { reportType, format, params }, `Exported ${reportType} as ${format}`);

/** Errors */
export const trailError = (module, errorMessage, context = {}) =>
    logTrail(TRAIL_EVENTS.ERROR, module, { message: errorMessage, ...context });

export const trailApiError = (module, endpoint, statusCode, errorMessage) =>
    logTrail(TRAIL_EVENTS.API_FAIL, module, { endpoint, statusCode, message: errorMessage });

// ─── QoC-specific wrappers ────────────────────────────────────

/** Assessment lifecycle events */
export const trailAssessmentSubmit = (facilityName, assessmentId, period) =>
    logTrail(TRAIL_EVENTS.ASSESSMENT_SUBMIT, TRAIL_MODULES.ASSESSMENTS,
        { facilityName, assessmentId, period },
        `Submitted assessment for ${facilityName}`);

export const trailAssessmentApprove = (facilityName, assessmentId) =>
    logTrail(TRAIL_EVENTS.ASSESSMENT_APPROVE, TRAIL_MODULES.APPROVAL_QUEUE,
        { facilityName, assessmentId },
        `Approved assessment for ${facilityName}`);

export const trailAssessmentReject = (facilityName, assessmentId, reason) =>
    logTrail(TRAIL_EVENTS.ASSESSMENT_REJECT, TRAIL_MODULES.APPROVAL_QUEUE,
        { facilityName, assessmentId, reason },
        `Rejected assessment for ${facilityName}`);

export const trailAssessmentRevision = (facilityName, assessmentId, reason) =>
    logTrail(TRAIL_EVENTS.ASSESSMENT_REVISION, TRAIL_MODULES.APPROVAL_QUEUE,
        { facilityName, assessmentId, reason },
        `Requested revision for ${facilityName}`);

/** CAP */
export const trailCapCreate = (facilityName, capId, clauseRef) =>
    logTrail(TRAIL_EVENTS.CAP_CREATE, TRAIL_MODULES.CAP,
        { facilityName, capId, clauseRef },
        `Created CAP item for ${facilityName}`);

export const trailCapUpdate = (capId, field, oldValue, newValue) =>
    logTrail(TRAIL_EVENTS.CAP_UPDATE, TRAIL_MODULES.CAP,
        { capId, field, oldValue, newValue },
        `Updated CAP #${capId}`);

/** HIE Sync */
export const trailHieSync = (facilityCount, status, detail = '') =>
    logTrail(TRAIL_EVENTS.HIE_SYNC, TRAIL_MODULES.HIE_SYNC,
        { facilityCount, status, detail },
        `HIE sync: ${status} (${facilityCount} facilities)`);

/** Auth — called from Login.jsx / logout handler */
export const trailLogin = (username) =>
    logTrail(TRAIL_EVENTS.LOGIN, 'Auth', { username }, `Login: ${username}`);

export const trailLogout = (username) =>
    logTrail(TRAIL_EVENTS.LOGOUT, 'Auth', { username }, `Logout: ${username}`);